import { useCallback, useRef, useContext, useMemo, useEffect } from 'react';
import { useShallow } from 'zustand/shallow';
import type { Editor } from '@tiptap/core';
import * as Sentry from '@sentry/react';
import type { EditRequest, ReviewMode } from '@/lib/ai-composer/types';
import type { EditEntityKind } from '@/lib/ai-composer/types';
import type { EditApplierRegistry, PendingEdit, RequestingPending } from '@/lib/ai-composer/applier-registry';
import { AiComposerRegistryContext } from '@/lib/ai-composer/ai-composer-context';
import {
  EditActionTransportContext,
  type EditActionTransport,
} from '@/lib/ai-composer/edit-action-transport-context';
import { consumeEditStream } from '@/lib/ai-composer/edit-stream-consumer';
import { useEditActionStore } from '../stores/use-edit-action-store';
import { capture } from '@/lib/analytics/capture';
import { dispositionForAccept } from '../utils/ai-edit-disposition';

const defaultSchedule = (cb: () => void, ms: number): ReturnType<typeof setTimeout> => setTimeout(cb, ms);

/** Per-delta pause that paces the AI text reveal to a calmer, ~half-speed rate. */
const REVEAL_DELAY_MS = 30;

export interface UseEditActionDeps {
  transport: EditActionTransport;
  registry: EditApplierRegistry;
  captureError: (e: unknown) => void;
  schedule?: (cb: () => void, ms: number) => ReturnType<typeof setTimeout>;
}

export function useEditAction(
  _editor: Editor | null,
  entityId: string,
  entityKind: EditEntityKind,
  deps?: UseEditActionDeps,
) {
  const contextTransport = useContext(EditActionTransportContext);
  const contextRegistry = useContext(AiComposerRegistryContext);

  const transport = deps?.transport ?? contextTransport;
  const registry = deps?.registry ?? contextRegistry;
  const captureErrorFn = deps?.captureError;
  const captureError = useMemo(
    () => captureErrorFn ?? ((e: unknown) => Sentry.captureException(e)),
    [captureErrorFn],
  );
  const scheduleRef = useRef(deps?.schedule ?? defaultSchedule);
  scheduleRef.current = deps?.schedule ?? defaultSchedule;
  const schedule = scheduleRef.current;

  // C5: subscribe to stable action refs via useShallow so callbacks don't
  // recreate on every state change (store object identity changes each render).
  const { toRequesting, start, fail, reset } = useEditActionStore(
    useShallow((s) => ({ toRequesting: s.toRequesting, start: s.start, fail: s.fail, reset: s.reset })),
  );

  const entityState = useEditActionStore((s) => s.byEntity[entityId]);
  const status = entityState?.status ?? 'idle';
  const pending = entityState?.pending as PendingEdit | undefined;

  const suppressDiscardEventRef = useRef(false);
  const lastReqRef = useRef<{ req: EditRequest; blockContent: string; mode: ReviewMode } | null>(null);

  const lastModeRef = useRef<ReviewMode>('ghost-write');
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cancel-on-unmount support: the real pending lives inside consumeEditStream, never the
  // store (the store holds only the requesting stub mid-stream). Track it here so unmount
  // can reject it synchronously (restoring the original) and abort the dangling stream.
  const inFlightPendingRef = useRef<PendingEdit | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // The applier instance captured at run() time. On unmount, useRegisterEditApplier's
  // cleanup has often ALREADY deregistered the applier from the registry (sibling-effect
  // teardown order), so re-resolving via registry.forKind() returns undefined and the
  // cancel silently no-ops. Hold the instance directly so cancel works regardless.
  const applierRef = useRef<ReturnType<EditApplierRegistry['forKind']> | null>(null);

  const run = useCallback(
    async (req: EditRequest, blockContent: string, mode?: ReviewMode) => {
      if (!transport || !registry) return;
      // Concurrency guard — block only while a run is actively in flight. 'error' is a
      // recoverable terminal state: a new run must be able to start from it (otherwise a
      // single failed run wedges the editor's AI actions until the app restarts).
      const currentStatus = useEditActionStore.getState().byEntity[entityId]?.status ?? 'idle';
      if (currentStatus === 'requesting' || currentStatus === 'streaming' || currentStatus === 'review') return;

      // Default mode: chart → embed, text → ghost-write (preserves backward compat).
      const resolvedMode: ReviewMode = mode ?? (req.action === 'generate-chart' ? 'embed' : 'ghost-write');
      lastModeRef.current = resolvedMode;
      lastReqRef.current = { req, blockContent, mode: resolvedMode };
      // corrId MUST be a UUID — it becomes the envelope corrId (16-byte UUID field) and the
      // key both nexus and the response-matcher (corrIdMatches, 32 hex chars) rely on. A
      // non-UUID like `run-N` throws in encodeEnvelope ('invalid UUID') and never matches.
      const corrId = crypto.randomUUID();
      const to = req.target.kind === 'tiptap-range' ? req.target.to : 0;
      const stubKind: RequestingPending['kind'] = req.action === 'generate-chart' ? 'canvas' : 'text';
      const requestingStub: RequestingPending = { id: corrId, kind: stubKind, to };
      toRequesting(entityId, requestingStub);

      // Stall guard: if the LLM never sends a first chunk within 15 s, flip to error.
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      stallTimerRef.current = schedule(() => {
        const currentState = useEditActionStore.getState().byEntity[entityId]?.status ?? 'idle';
        if (currentState !== 'requesting') return;
        const stallErr = new Error('The AI did not respond. Try again.');
        fail(entityId, stallErr.message);
        captureError(stallErr);
      }, 15_000);

      // Chart has no streaming text — show its loading widget upfront (before the LLM
      // call), then reuse that pending so the consumer doesn't begin() twice. If the
      // run errors, the consumer rejects this pending so the widget never sticks.
      const initialPending =
        req.action === 'generate-chart' ? registry.forKind(entityKind)?.begin(req) : undefined;
      const abort = new AbortController();
      abortRef.current = abort;
      inFlightPendingRef.current = initialPending ?? null;
      applierRef.current = registry.forKind(entityKind) ?? null;
      try {
        const stream = await transport.openEditActionStream(corrId, req, blockContent);
        if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
        start(entityId, { id: corrId });
        const resolvedPending = await consumeEditStream(
          stream, req, corrId, registry, initialPending, resolvedMode, REVEAL_DELAY_MS, undefined,
          abort.signal,
          (p) => { inFlightPendingRef.current = p; },
        );
        // Cancelled mid-stream (tab switch / document close): the unmount cleanup already
        // rejected the pending and reset the store. Do NOT route the resolved pending —
        // re-creating review/accept state here would resurface a stale tooltip on reopen.
        if (abort.signal.aborted) return;
        if (resolvedPending) {
          // Mode-driven routing: ghost-write/disabled/embed auto-apply; preview/review go
          // to the review status so the tooltip can gate acceptance. Embed auto-applies
          // because the canvas embed + entity already exist — there's nothing to confirm.
          if (resolvedMode === 'ghost-write' || resolvedMode === 'disabled' || resolvedMode === 'embed') {
            // reset() must run even if accept() throws, so a finalize failure can't leave
            // the entity stuck in a non-idle state that blocks every subsequent run.
            try {
              registry.forKind(entityKind)?.accept(resolvedPending);
              capture('ai_edit_resolved', {
                disposition: dispositionForAccept(resolvedMode),
                action: req.action,
                entity_kind: entityKind,
                mode: resolvedMode,
              });
            } finally {
              // Finalized — nothing left to cancel on unmount.
              inFlightPendingRef.current = null;
              abortRef.current = null;
              applierRef.current = null;
              reset(entityId);
            }
          } else {
            registry.forKind(entityKind)?.freezePreview?.(resolvedPending);
            // Keep the in-flight handle pointing at the resolved pending so an unmount while
            // it sits in review still cancels (rejects) it.
            inFlightPendingRef.current = resolvedPending;
            abortRef.current = null;
            // C3: atomic single setState — set status:'review' AND pending together.
            useEditActionStore.setState((s) => ({
              byEntity: {
                ...s.byEntity,
                [entityId]: { ...s.byEntity[entityId], status: 'review', pending: resolvedPending },
              },
            }));
          }
        } else {
          inFlightPendingRef.current = null;
          abortRef.current = null;
          applierRef.current = null;
          reset(entityId);
        }
      } catch (e) {
        if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
        // The consumer's finally already rejected any pending on throw — nothing to restore.
        inFlightPendingRef.current = null;
        abortRef.current = null;
        applierRef.current = null;
        // A throw caused by cancellation (tab switch / close) is not a real error — the
        // cleanup already reset the store. Don't flip to 'error' (would leak on reopen) or
        // report it to Sentry.
        if (abort.signal.aborted) return;
        const msg = e instanceof Error ? e.message : String(e);
        fail(entityId, msg);
        captureError(e);
      }
    },
    [transport, registry, captureError, schedule, entityId, entityKind, toRequesting, start, fail, reset],
  );

  const accept = useCallback(() => {
    if (!registry) return;
    const livePending = useEditActionStore.getState().byEntity[entityId]?.pending as PendingEdit | undefined;
    if (!livePending) return;
    const applier = registry.forKind(entityKind);
    if (applier) applier.accept(livePending);
    capture('ai_edit_resolved', {
      disposition: 'accepted',
      action: lastReqRef.current?.req.action ?? 'unknown',
      entity_kind: entityKind,
      mode: lastModeRef.current ?? 'unknown',
    });
    inFlightPendingRef.current = null;
    abortRef.current = null;
    applierRef.current = null;
    reset(entityId);
  }, [registry, entityKind, entityId, reset]);

  const discard = useCallback(() => {
    if (!registry) return;
    const livePending = useEditActionStore.getState().byEntity[entityId]?.pending as PendingEdit | undefined;
    if (!livePending) return;
    const applier = registry.forKind(entityKind);
    if (applier) applier.reject(livePending);
    if (!suppressDiscardEventRef.current) {
      capture('ai_edit_resolved', {
        disposition: 'discarded',
        action: lastReqRef.current?.req.action ?? 'unknown',
        entity_kind: entityKind,
        mode: lastModeRef.current ?? 'unknown',
      });
    }
    inFlightPendingRef.current = null;
    abortRef.current = null;
    applierRef.current = null;
    reset(entityId);
  }, [registry, entityKind, entityId, reset]);

  const retry = useCallback(() => {
    const last = lastReqRef.current;
    if (!last) return;
    // retry() calls discard() internally; suppress that event so one user
    // action produces one disposition rather than a discard plus a retry.
    suppressDiscardEventRef.current = true;
    try {
      discard();
    } finally {
      suppressDiscardEventRef.current = false;
    }
    capture('ai_edit_resolved', {
      disposition: 'retried',
      action: last.req.action,
      entity_kind: entityKind,
      mode: last.mode,
    });
    void run(last.req, last.blockContent, last.mode);
  }, [discard, run, entityKind]);

  // Cancel-on-unmount: switching tabs unmounts the editor mid-edit. Abort the dangling
  // stream and reject the in-flight pending (via applierRef, captured at run() time) so the
  // original is restored. Reject runs synchronously here, before Tiptap destroys the editor
  // (it schedules destroy on a timeout), so the restore transaction can still dispatch.
  // entityIdRef keeps the [] cleanup from capturing a stale entityId across re-renders.
  const entityIdRef = useRef(entityId);
  entityIdRef.current = entityId;
  useEffect(() => () => {
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    abortRef.current?.abort();
    abortRef.current = null;
    const live = inFlightPendingRef.current;
    inFlightPendingRef.current = null;
    // Use the applier captured at run() time, NOT registry.forKind() — by now the registry
    // has usually deregistered it (sibling-effect teardown order), which would no-op cancel.
    const applierForCancel = applierRef.current;
    applierRef.current = null;
    if (live) applierForCancel?.reject(live);
    useEditActionStore.getState().reset(entityIdRef.current);
  }, []);

  return { run, accept, discard, retry, status, pending };
}
